/* eslint-disable no-await-in-loop */
/* eslint-disable no-nested-ternary */
import { css } from '@emotion/core'
import styled from '@emotion/styled'
import React, { ClipboardEvent, useEffect, useRef, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import useStateRef from 'react-usestateref'
import withProps from 'recompose/withProps'
import IcDisclosure from 'src/static/ic-textarea-disclosure.png'
import SvgFollowUp from 'src/static/components/SvgFollowUp'
import { avaReplaceQuestionAction, avaSettingsSelectors } from 'src/redux'
import SvgReplaceQuestion from 'src/static/components/SvgReplaceQuestion'
import useBranding from 'src/utils/useBranding'
import * as interviewSelectors from 'src/interview/selectors'
import {
  BaseDisable,
  ButtonBase,
  NavButton,
  QuestionCounterContainer,
  WarningContainer,
  TitleText,
} from '../elements/styled-components'
import BottomComponent from '../intro/BottomComponent'
import useAvaSound from '../../services/avaSound'
import ChangeQuestionPopup from './ChangeQuestionPopup'
import LiveText from '../LiveText'
import BallsAnimation from './BallsAnimation'
import MultilineLiveText from '../MultilineLiveText'
import LoadingBar from '../elements/LoadingBar'

import useInterview, { MinCharCount, Warnings } from '../../services/useInterview'

const AnimationFirstSpeedMs = 166
const AnimationLastSpeedMs = 2000

const Container = styled.div<{ nonFullHeight?: boolean }>`
  width: 100%;
  flex: 1;
  padding: 0 64px;
  display: flex;
  flex-direction: column;

  ${isMobileOnly &&
  css`
    background: white;
    padding: 32px 16px 0 16px;
    border-radius: 24px 24px 0px 0px;
  `}
`

const LineContainer = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: row;
  flex-basis: 0;
  min-height: 0;
`

const ButtonsContainer = styled(BaseDisable)`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 55px;
  margin-top: 42px;
  ${isMobileOnly &&
  css`
    margin-top: 17px;
    margin-bottom: 16px;
  `}
`

const TextAreaContainer = styled(BaseDisable)<{ collapsed: boolean }>`
  background: white;
  position: relative;
  padding: 24px 23px 0 32px;
  border-radius: 28px;
  height: ${({ collapsed }) => (!isMobileOnly ? (collapsed ? '69px' : '232px') : '100%')};
  max-height: ${({ collapsed }) =>
    isMobileOnly ? (collapsed ? '69px' : '400px') : 'unset'};
  flex: ${isMobileOnly ? '1' : 'unset'};
  transition: all 0.5s;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 0px 25px 5px rgb(0 0 0 / 7%);
  pointer-events: all;
`

const TextArea = styled.textarea<{ disabled?: boolean; collapsed: boolean }>`
  background: transparent;
  border: none;
  margin-right: ${({ collapsed }) => (collapsed ? 290 : 0)}px;
  margin-bottom: ${({ collapsed }) => (collapsed ? 18 : isMobileOnly ? 10 : 40)}px;
  overflow: ${({ collapsed }) => (collapsed ? 'hidden' : 'visible')};
  flex: 1;
  width: 100%;
  font-weight: 400;
  line-height: 150%;
  color: black;
  outline: none;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
  opacity: ${({ disabled }) => (disabled ? '0.6' : '1')};
  font-size: 14px;
  resize: none;
  padding: 0;

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.2);
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.3);
  }
`

const StaticTextArea = styled.div<{ empty?: boolean }>`
  background: transparent;
  margin-right: 290px;
  margin-bottom: 18px;
  flex: 1;
  width: 100%;
  font-weight: 400;
  line-height: 150%;
  color: black;
  font-size: 14px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  opacity: ${({ empty }) => (empty ? 0.5 : 1)};

  ${isMobileOnly &&
  css`
    margin-right: 0;
  `}
`

const CharCounterLabel = styled.div<{ collapsed: boolean }>`
  position: absolute;
  bottom: ${({ collapsed }) => (collapsed ? 25 : 18)}px;
  right: 56px;
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  width: fit-content;
  align-self: flex-end;
  color: #000000;
  opacity: 0.5;

  ${isMobileOnly &&
  css`
    position: unset;
    margin-top: 4px;
    font-size: 10px;
    line-height: 15px;
  `}
`

const DisclosureButton = styled.img<{ collapsed: boolean }>`
  width: 25px;
  height: 25px;
  padding: 0 7px;
  object-fit: contain;
  cursor: pointer;
  border-radius: 13px;
  transform: rotate(${({ collapsed }) => (collapsed ? 180 : 0)}deg);
  transition: transform 0.5s;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
  &:active {
    background: rgba(0, 0, 0, 0.1);
  }
`

const AnimatedContainer = styled.div`
  position: relative;
  width: 100%;
  animation: fadeInFromNone 0.5s ease-out;
  margin-bottom: ${isMobileOnly ? 0 : 72}px;
  display: flex;
  flex-direction: column;
  flex: 1;

  &:placeholder {
    opacity: 0.8;
  }

  @keyframes fadeInFromNone {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
`

const ReplaceQuestionContainer = styled.div`
  display: flex;
`

const ReplaceQuestionButton = withProps()(styled(ButtonBase)`
  position: relative;
  color: ${({ theme }: any) => {
    const color =
      !theme.agencyColor || theme.agencyColor === '' ? '#B202FF' : theme.agencyColor
    return color
  }};
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  margin-left: 8px;

  ${isMobileOnly &&
  css`
    width: fit-content;
  `}

  &:hover {
    text-decoration: underline;
  }
`)

const BottomWrapper = styled.div`
  margin-top: auto;
`

const TitleTextStyled = styled(TitleText)`
  font-size: 36px;
  line-height: 50px;
  margin-bottom: 8px;

  ${isMobileOnly &&
  css`
    font-size: 20px;
    line-height: 28px;
  `}
`

const PreTitleText = styled(BaseDisable)`
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  color: #000;

  ${isMobileOnly &&
  css`
    font-size: 14px;
    line-height: 20px;
  `}
`

const GoButton = styled(NavButton)`
  height: 55px;
  margin-top: auto;
  margin-bottom: ${isMobileOnly ? '16px' : '8px'};
  width: 100%;
`

const LoadingContainer = styled.div`
  position: absolute;
  width: 100%;
  height: calc(100% - 72px);
  left: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  top: 72px;
  border-radius: 24px 24px 0px 0px;

  ${isMobileOnly &&
  `
    padding: 0 20px;
  `}
`

const LoadingText = styled.div`
  color: #000;
  font-family: Rubik;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  margin-top: 32px;

  ${isMobileOnly &&
  `
    text-align: center;
    font-size: 20px;
  `}
`

const WarningActiveButton = styled.div`
  align-items: center;
  display: flex;
  height: 24px;
  border-radius: 4px;
  background: #b202ff;
  color: white;
  cursor: pointer;
  padding: 0px 10px;
  font-weight: 500;
  font-size: 12px;
  line-height: 24px;
  justify-content: center;

  &:active {
    opacity: 0.5;
  }

  svg {
    margin-right: 10px;
  }
`

const WarningPassiveButton = styled.div`
  align-items: center;
  display: flex;
  height: 24px;
  border: 1px solid white;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  padding: 0px 10px;
  font-weight: 500;
  font-size: 12px;
  line-height: 24px;
  justify-content: center;
  margin-right: 10px;

  &:active {
    opacity: 0.5;
  }

  svg {
    margin-right: 10px;
  }
`

const WarningButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  width: 100%;
`

const SubmittedTag = styled.div`
  color: #a9a9a9;
  border-radius: 5px;
  padding: 2px 5px;
  margin-left: 10px;
  background: #ededed;
  text-transform: none;
`

enum PopupState {
  NoShown,
  Show,
  Shown,
}

interface IProps {
  clearReportClicked: () => void
  reportClicked: boolean
}

const LoadItems = ['Analyzing your answer...', 'Generating follow-up question']

const AIQuestionComponent = ({ clearReportClicked, reportClicked }: IProps) => {
  const dispatch = useDispatch()
  const { interviewToken } = useParams<{ interviewToken?: string }>()

  const { playSound } = useAvaSound()
  const { agencyColor } = useBranding()
  const {
    canGoBack,
    checkAnswers,
    clearWarning,
    dismissWarning,
    goBack,
    goNext,
    goNextUnanswered,
    goReport,
    needAnswers,
    question,
    questionCount,
    questionIndex,
    saveAnswer,
    setWarning,
    warningRef,
  } = useInterview(interviewToken)
  const warning = warningRef.current

  const avaSettings = useSelector(avaSettingsSelectors.data)
  const agency = useSelector(interviewSelectors.agency)
  const [loading, setLoading] = useState(false)

  const [textValue, setTextValue, textValueRef] = useStateRef<string>()
  const [showInput, setShowInput] = useState(false)
  const [collapsed, setCollapsed] = useState(true)
  const [showChangeQuestionPopup, setShowChangeQuestionPopup] = useState(
    PopupState.NoShown
  )
  const [progress, setProgress, progressRef] = useStateRef(0)
  const [loadingText, setLoadingText] = useState(LoadItems[0])
  const [animationSpeedMs, setAnimationSpeedMs] = useState(0)

  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const useTouch = useRef(false)
  const questionRef = useRef(undefined)

  const isFollowUp = !!question?.parent_id

  const answered = !!question?.answer?.text

  const getInterviewQuestionIndexText = () => {
    if (!question) return ''
    const normalizeNumber = (num: number) => {
      return `${num < 10 ? '0' : ''}${num}`
    }

    return `${normalizeNumber(questionIndex + 1)}/${normalizeNumber(questionCount)} - ${
      question.question_type
    } Question${question.parent_id ? '- follow up' : ''}`
  }

  const runProgress = async () => {
    while (progressRef.current < 0.91) {
      await new Promise((resolve) => setTimeout(resolve, AnimationFirstSpeedMs))
      setProgress((v) => v + 0.01)
    }
    while (progressRef.current < 0.99) {
      await new Promise((resolve) => setTimeout(resolve, AnimationLastSpeedMs))
      setProgress((v) => v + 0.01)
    }
  }

  const formAnswer = () => {
    setCollapsed(false)
  }

  const onNext = () => {
    clearReportClicked()
    goNext()
  }

  const handleNextClick = () => {
    if (!answered) {
      formAnswer()
      checkAnswers(textValueRef.current)
      if (textValueRef.current && textValueRef.current.length >= MinCharCount) {
        saveAnswer(textValueRef.current)
      }

      if (!warningRef.current || reportClicked) {
        if (question.order === 1 && textValueRef.current) setLoading(true)
        else onNext()
      }
    } else {
      checkAnswers()
      if (!warningRef.current) onNext()
    }
  }

  const handleReplaceQuestion = () => {
    dispatch(
      avaReplaceQuestionAction({
        interviewToken,
        questionId: question.id,
      })
    )

    setShowInput(false)
    setShowChangeQuestionPopup(PopupState.Shown)
    setTextValue('')
  }

  const handleReplaceQuestionButton = () => {
    if (showChangeQuestionPopup === PopupState.Shown) {
      handleReplaceQuestion()
    } else {
      setShowChangeQuestionPopup(PopupState.Show)
    }
  }

  const handleCopyPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (agency?.disable_copy_paste_answer) {
      e.preventDefault()
      alert('Copying and pasting is not allowed!')
    }
  }

  const handleAnswerMore = () => {
    clearReportClicked()
    if (!answered && textValueRef.current && textValueRef.current.length >= MinCharCount)
      saveAnswer(textValueRef.current)

    if (textValueRef.current) goNextUnanswered()
    else clearWarning(Warnings.FewAnswers)
  }

  const handleGoToReport = () => {
    if (!answered && textValueRef.current && textValueRef.current.length >= MinCharCount)
      saveAnswer(textValueRef.current)

    if (reportClicked) goReport()
    else onNext()

    clearReportClicked()
  }

  useEffect(() => {
    if (question && questionRef.current?.text !== question.text) {
      if (question.order !== 1) {
        if (progress !== 0) {
          setProgress(1)
          setTimeout(() => {
            setLoading(false)
          }, 1000)
        } else {
          setLoading(false)
        }
      }
      setShowInput(!!question.answer?.text)
      setCollapsed(!answered)
      setTextValue(question.answer?.text || '')
    }
    questionRef.current = question

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question])

  useEffect(() => {
    if (showInput) {
      playSound('/static/sound/ava-go-input.wav')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInput])

  useEffect(() => {
    if (!collapsed && textAreaRef.current) {
      textAreaRef.current.focus()
      textAreaRef.current.setSelectionRange(
        textAreaRef.current.value.length,
        textAreaRef.current.value.length
      )
    }
  }, [collapsed])

  useEffect(() => {
    window.onscroll = () => {
      if (useTouch.current) {
        ;(document.activeElement as HTMLElement).blur()
      }
    }
  }, [])

  useEffect(() => {
    if (loading) {
      runProgress()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  useEffect(() => {
    if (progress > 1) setProgress(1)
    if (progress < 0.54) {
      setLoadingText(LoadItems[0])
    } else {
      setLoadingText(LoadItems[1])
    }

    // Skip animation speed when fastTrack is enabled
    if (avaSettings.fastTrack) {
      setAnimationSpeedMs(0)
    } else if (progress < 0.91 || progress === 1) {
      setAnimationSpeedMs(AnimationFirstSpeedMs)
    } else {
      setAnimationSpeedMs(AnimationLastSpeedMs)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress])

  useEffect(() => {
    if (reportClicked) {
      checkAnswers(undefined, true)
      if (!warningRef.current) goReport()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportClicked])

  const actualString = textValue || ''
  const Placeholder = 'Type your answer here'

  return (
    <>
      <Container
        onTouchStart={() => {
          useTouch.current = true
        }}
        onTouchEnd={() => {
          useTouch.current = false
        }}
      >
        <QuestionCounterContainer>
          {isFollowUp && <SvgFollowUp fill="#A9A9A9" />}
          {getInterviewQuestionIndexText()}
          {answered && <SubmittedTag>Submitted</SubmittedTag>}
        </QuestionCounterContainer>

        {!isFollowUp && (
          <TitleTextStyled disabled={loading}>
            <LiveText
              text={question?.text}
              staticText={answered}
              onFinish={() => setShowInput(true)}
            />
          </TitleTextStyled>
        )}
        {isFollowUp && (
          <MultilineLiveText
            text={[question?.intro, question?.text].join('')}
            lines={[question?.intro, question?.text]}
            render={(lines) => (
              <>
                {lines[0] && <PreTitleText disabled={loading}>{lines[0]}</PreTitleText>}
                {lines[1] && (
                  <TitleTextStyled disabled={loading}>{lines[1]}</TitleTextStyled>
                )}
              </>
            )}
            staticText={answered}
            onFinish={() => setShowInput(true)}
          />
        )}

        {showInput && (
          <>
            <AnimatedContainer>
              <TextAreaContainer collapsed={collapsed} disabled={loading || answered}>
                <LineContainer
                  onClick={
                    answered
                      ? () => {
                          setWarning(Warnings.HasAnswer)
                        }
                      : undefined
                  }
                >
                  {!collapsed && (
                    <TextArea
                      maxLength={2500}
                      ref={textAreaRef}
                      collapsed={collapsed}
                      rows={isMobileOnly ? 2 : 4}
                      disabled={answered}
                      value={actualString}
                      placeholder={Placeholder}
                      onChange={(ev) => {
                        clearReportClicked()
                        setWarning(undefined)
                        setTextValue(ev.target.value)
                        setCollapsed(false)
                      }}
                      onCopy={handleCopyPaste}
                      onCut={handleCopyPaste}
                      onPaste={handleCopyPaste}
                    />
                  )}
                  {collapsed && (
                    <StaticTextArea
                      empty={actualString === ''}
                      onClick={() => setCollapsed(false)}
                    >
                      {actualString !== '' ? actualString : Placeholder}
                    </StaticTextArea>
                  )}
                  <DisclosureButton
                    collapsed={collapsed}
                    src={IcDisclosure}
                    alt="disclosure"
                    onClick={() => setCollapsed((v) => !v)}
                  />
                </LineContainer>
                {!isMobileOnly && (
                  <CharCounterLabel collapsed={collapsed}>
                    {actualString.length}/{MinCharCount} (You must have minimum of{' '}
                    {MinCharCount} chars)
                  </CharCounterLabel>
                )}
              </TextAreaContainer>
              {isMobileOnly && (
                <CharCounterLabel collapsed={collapsed}>
                  {actualString.length}/{MinCharCount} (You must have minimum of{' '}
                  {MinCharCount} chars)
                </CharCounterLabel>
              )}
              <ButtonsContainer disabled={loading || answered}>
                <ReplaceQuestionContainer>
                  <SvgReplaceQuestion fill={agencyColor} />
                  <ReplaceQuestionButton
                    disabled={loading || answered}
                    onClick={handleReplaceQuestionButton}
                  >
                    Replace Question
                  </ReplaceQuestionButton>
                </ReplaceQuestionContainer>
              </ButtonsContainer>
              {!isMobileOnly && (
                <BottomWrapper>
                  <BottomComponent
                    onNext={handleNextClick}
                    showBackButton={canGoBack}
                    onBack={goBack}
                    loading={loading}
                    loadingText="Analyzing your answer"
                  />
                </BottomWrapper>
              )}
              {isMobileOnly && (
                <GoButton onClick={handleNextClick} hideIcon>
                  {loading ? <BallsAnimation /> : 'Next'}
                </GoButton>
              )}
            </AnimatedContainer>
            {warning && (
              <WarningContainer
                top={reportClicked}
                large={warning === Warnings.FewAnswers}
              >
                {warning === Warnings.AnswerSize &&
                  `To get valuable insights, your response should have a minimum of ${MinCharCount} characters`}
                {warning === Warnings.NoAnswers &&
                  'To complete your session, answer at least one question.'}
                {warning === Warnings.FewAnswers &&
                  `To get the most from your session, we recommend answering at least ${needAnswers(
                    ''
                  )} more question${needAnswers('') > 1 ? 's' : ''}.`}
                {warning === Warnings.HasAnswer && 'Submitted answers cannot be edited.'}

                <WarningButtons>
                  {warning === Warnings.AnswerSize && (
                    <>
                      <WarningPassiveButton
                        onClick={() => dismissWarning(Warnings.AnswerSize)}
                      >
                        <span>Skip question</span>
                      </WarningPassiveButton>
                      <WarningActiveButton
                        onClick={() => clearWarning(Warnings.AnswerSize)}
                      >
                        <span>Edit my answer</span>
                      </WarningActiveButton>
                    </>
                  )}
                  {warning === Warnings.NoAnswers && (
                    <WarningActiveButton
                      onClick={() => {
                        clearReportClicked()
                        clearWarning(Warnings.NoAnswers)
                      }}
                    >
                      <span>Dismiss</span>
                    </WarningActiveButton>
                  )}
                  {warning === Warnings.FewAnswers && (
                    <>
                      <WarningPassiveButton onClick={handleAnswerMore}>
                        <span>Answer more questions</span>
                      </WarningPassiveButton>
                      <WarningActiveButton onClick={handleGoToReport}>
                        <span>Submit & view report</span>
                      </WarningActiveButton>
                    </>
                  )}
                  {warning === Warnings.HasAnswer && (
                    <WarningActiveButton
                      onClick={() => {
                        dismissWarning(Warnings.HasAnswer)
                      }}
                    >
                      <span>Dismiss</span>
                    </WarningActiveButton>
                  )}
                </WarningButtons>
              </WarningContainer>
            )}
          </>
        )}
      </Container>
      {showChangeQuestionPopup === PopupState.Show && (
        <ChangeQuestionPopup
          onClose={() => setShowChangeQuestionPopup(PopupState.Shown)}
          onChange={handleReplaceQuestion}
        />
      )}
      {loading && (
        <LoadingContainer>
          <LoadingBar progress={progress} animationSpeedMs={animationSpeedMs} />
          <LoadingText>{loadingText}</LoadingText>
        </LoadingContainer>
      )}
    </>
  )
}

export default AIQuestionComponent
